/**
 * How a file's place among its versions is said (`document-ai/0016`).
 *
 * Shared by the document viewer and the Brain's rows, so the two never word
 * the same role differently. The labels themselves are the contract's
 * (`FAMILY_ROLE_LABELS`, `PLACEMENT_RELATION_LABELS`); this only joins them to
 * the file they point at.
 */
import {
  FAMILY_ROLE_LABELS,
  PLACEMENT_RELATION_LABELS,
  roleAnswers,
  type FamilyRole,
  type PlacementRelation,
} from "@gb/contracts";
import { formatDate } from "@/lib/format";

/**
 * The word between a role and the file it defers to: "Superseded by …",
 * "Another format of …". Only the roles that do not answer point at a file.
 */
const RELATED_WORD: Record<FamilyRole, string | null> = {
  current: null,
  "series-member": null,
  superseded: "by",
  "alternative-format": "of",
  "signature-state": "of",
  "identical-copy": "of",
};

/** "Superseded by", "Identical copy of". Null for a role that answers. */
export function roleLead(role: FamilyRole): string | null {
  const word = RELATED_WORD[role];
  return roleAnswers(role) || !word ? null : `${FAMILY_ROLE_LABELS[role]} ${word}`;
}

/**
 * The role as one plain line: "Superseded by “Ramming plan rev 02”", or
 * "One of a series". The related file is left out when it is not known.
 */
export function roleSentence(role: FamilyRole, relatedName: string | null): string {
  const lead = roleLead(role);
  return lead && relatedName ? `${lead} “${relatedName}”` : FAMILY_ROLE_LABELS[role];
}

/**
 * A relation, lower-cased to sit inside a sentence: "an older version of".
 * The labels are written to follow "This file is …".
 */
export function relationPhrase(relation: PlacementRelation): string {
  const label = PLACEMENT_RELATION_LABELS[relation];
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * The date a document states, for a person: "26 Aug 2026".
 *
 * The contract sends an ISO date with no time. Anything else is shown as it
 * came, rather than as "Invalid Date".
 */
export function formatDocumentDate(iso: string): string {
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return iso;
  const formatted = formatDate(day);
  return formatted === "Invalid Date" ? iso : formatted;
}

/**
 * The revision a document states, for a person: "Revision 02".
 *
 * It is kept as written, so a revision that already says so ("Rev C") is not
 * prefixed a second time.
 */
export function revisionText(revision: string): string {
  return /^rev/i.test(revision.trim()) ? revision : `Revision ${revision}`;
}
